import React, { useState, useEffect } from 'react';
import { Input } from '../components/UI';
import * as api from '../api';
import AuthBackdrop from '../components/AuthBackdrop';
import MfaSignIn from '../components/MfaSignIn';

export default function LoginPage({ onLogin, theme, setTheme }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: false });
  // Two-step sign-in in progress: { mode: 'verify' | 'setup', token }
  const [mfa, setMfa] = useState(null);

  // Apply OS zoom hack specifically for the Login Page, and clean it up when leaving.
  useEffect(() => {
    const isWindows = navigator.userAgent.indexOf('Win') !== -1;
    const dpr = window.devicePixelRatio || 1;
    let originalZoom = document.documentElement.style.zoom;

    if (isWindows && dpr > 1 && dpr < 2) {
      document.documentElement.style.zoom = 1 / dpr;
    }

    return () => {
      document.documentElement.style.zoom = originalZoom;
    };
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  const isFormValid = email.length > 0 && pw.length > 0;

  const handle = async () => {
    if (!isFormValid) return;
    setLoading(true); setMsg({ text: '', ok: false });
    try {
      const data = await api.login(email, pw);
      // The account requires two-step sign-in: the password was right, the code comes next.
      if (data.mfa_required || data.mfa_setup_required) {
        setMfa({ mode: data.mfa_setup_required ? 'setup' : 'verify', token: data.mfa_token });
        setLoading(false);
        return;
      }
      finish(data);
    } catch (e) {
      setMsg({ text: e.message || 'Something went wrong.', ok: false });
    }
    setLoading(false);
  };

  const finish = (data) => {
    // An administrator-issued temporary password gets the holder in, but no
    // further — the password they just typed is known to whoever issued it,
    // so they are handed straight to the change screen. The password is
    // passed along because the change endpoint verifies the current one.
    onLogin(data.access_token, data.refresh_token, {
      mustChangePassword: !!data.must_change_password,
      currentPassword: pw,
    });
  };

  const isDark = true;

  return (
    <div style={{ minHeight: '100vh', boxSizing: 'border-box', display: 'flex', padding: 20, background: isDark ? '#0a0a0f' : '#f0f0f5', position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>

      <AuthBackdrop isDark={isDark} />

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 560, margin: 'auto', padding: '40px 0' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="login-logo-outer" style={{
            borderRadius: 24,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.4), 0 0 60px rgba(153,27,27,0.15)' : '0 12px 32px rgba(0,0,0,0.12)',
          }}>
            <img className="login-logo-inner" src="/rnl-logo.png" alt="ProjectFlow Logo" style={{ borderRadius: 16 }} />
          </div>
          <h1 className="login-title" style={{ fontWeight: 800, letterSpacing: '-0.04em', color: 'var(--text-primary)', textShadow: isDark ? '0 2px 20px rgba(0,0,0,0.5)' : 'none', margin: 0, textAlign: 'center' }}>Rifitel Nigeria Limited</h1>
          <div className="login-subtitle" style={{ fontWeight: 700, letterSpacing: '0.18em', color: '#6490f6ff', textShadow: isDark ? '0 0 20px rgba(153,27,27,0.4)' : 'none', textAlign: 'center' }}>MANAGEMENT SYSTEM</div>
        </div>

        <div className="login-box-padding" style={{
          background: isDark ? 'rgba(18, 18, 24, 0.65)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          width: '100%',
          border: `4px solid ${isDark ? '#9d97abff' : 'black'}`,
          borderRadius: 24,
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.5), 0 0 80px rgba(153,27,27,0.15), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 20px 50px rgba(0,0,0,0.12), 0 0 60px rgba(153,27,27,0.08), 0 0 0 1px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 6, letterSpacing: '0.02em' }}>
            {greeting}
          </div>
          <h2 className="login-form-title" style={{ fontWeight: 700, textAlign: 'center', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            Sign in to your account
          </h2>

          {mfa ? (
            <MfaSignIn mode={mfa.mode} mfaToken={mfa.token} onDone={finish}
                       onCancel={() => { setMfa(null); setPw(''); setMsg({ text: '', ok: false }); }} />
          ) : (
          <div className="login-input-gap" style={{ display: 'flex', flexDirection: 'column' }}>
            <Input label="Email address" type="email" value={email} onChange={setEmail} placeholder="you@company.com" required disabled={loading} style={{ padding: '12px 14px', fontSize: 14, border: '1px solid #6490f6ff', background: '#1e1e24', color: 'white', opacity: loading ? 0.6 : 1 }} />
            <Input label="Password" type="password" value={pw} onChange={setPw} placeholder="••••••••••••••••••••" required disabled={loading} style={{ padding: '12px 14px', fontSize: 14, border: '1px solid #6490f6ff', background: '#1e1e24', color: 'white', opacity: loading ? 0.6 : 1 }} />

            {msg.text && (
              <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 15, background: msg.ok ? 'rgba(100, 144, 246, 0.15)' : 'var(--red-dim)', color: msg.ok ? '#6490f6ff' : 'var(--red)', border: `1px solid ${msg.ok ? 'rgba(100, 144, 246, 0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                {msg.text}
              </div>
            )}

            <button
              onClick={handle}
              disabled={!isFormValid || loading}
              style={{
                width: '100%', marginTop: 8, padding: '12px 0', fontSize: 15, fontWeight: 'bold',
                background: '#6490f6ff', color: '#060505ff', border: 'none', borderRadius: 11,
                cursor: (!isFormValid || loading) ? 'not-allowed' : 'pointer',
                opacity: (!isFormValid || loading) ? 0.4 : 1, transition: 'all 0.2s ease',
                boxShadow: 'none', fontFamily: 'inherit'
              }}
              onMouseEnter={e => { if (isFormValid && !loading) { e.currentTarget.style.boxShadow = '0 0 18px #6490f6ff'; e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (isFormValid && !loading) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; } }}
            >
              {loading ? 'Authenticating...' : 'Login'}
            </button>

            {/* Self-service reset was withdrawn at the client's request. This is
                deliberately plain text and not a link — there is nowhere for it
                to go, and a link that only says "ask someone else" reads as
                broken. */}
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: 'rgba(255,255,255,0.62)', lineHeight: 1.6 }}>
              Forgot your password?{' '}
              <span style={{ color: '#6490f6ff', fontWeight: 700 }}>Contact the Administrator</span>
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: 'white', letterSpacing: '0.05em' }}>
          © {new Date().getFullYear()} Rifitel Nigeria · All rights reserved
        </div>

      </div>
    </div>
  );
}